import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useConversion } from '../hooks/useConversion';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import AnalyticsTestData from '../components/AnalyticsTestData';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Activity,
  MapPin,
  Clock,
  TrendingUp,
  Target,
  Award,
  Calendar,
  Map,
  Camera,
  Users,
  Star,
  Navigation,
  Mountain,
  Crown,
  ArrowRight,
  Shield,
  Zap
} from 'lucide-react';
import axios from 'axios';

const AnalyticsDashboard = () => {
  const { user } = useContext(AuthContext);
  const { showConversionPrompt } = useConversion();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      showConversionPrompt('analytics');
      return;
    }
    
    if (user.membership_tier === 'free') {
      showConversionPrompt('analytics');
      return;
    }

    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/analytics/dashboard`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || 'mock-token'}`
          }
        }
      );
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRecommendationClick = async (recommendationId, walkId) => {
    try {
      // Track the click
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/analytics/recommendations/${recommendationId}/click`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || 'mock-token'}`
          }
        }
      );
      
      // Navigate to the walk
      window.location.href = `/walks/${walkId}`;
    } catch (error) {
      console.error('Error tracking recommendation click:', error);
      // Still navigate even if tracking fails
      window.location.href = `/walks/${walkId}`;
    }
  };

  if (!user || user.membership_tier === 'free') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <Header />
        {/* Hero Illustration Section */}
        <section className="relative py-20 overflow-hidden">
          {/* Background illustration elements */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200/30 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-40 h-40 bg-indigo-200/30 rounded-full blur-xl animate-pulse delay-300"></div>
            <div className="absolute top-40 right-1/4 w-24 h-24 bg-purple-200/30 rounded-full blur-lg animate-pulse delay-700"></div>
          </div>
          
          <div className="relative max-w-6xl mx-auto px-4 text-center">
            {/* Main illustration */}
            <div className="mb-12">
              <div className="relative inline-block">
                <div className="w-80 h-60 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl shadow-2xl mx-auto relative overflow-hidden">
                  {/* Illustrated data background */}
                  <div className="absolute inset-0 bg-gradient-to-b from-blue-200 to-indigo-300 opacity-70"></div>
                  
                  {/* Chart lines */}
                  <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 320 80" className="w-full h-16 text-indigo-300">
                      <path d="M20,60 L60,40 L100,45 L140,25 L180,35 L220,20 L260,30 L300,15" stroke="currentColor" strokeWidth="3" fill="none" />
                    </svg>
                  </div>
                  
                  {/* Analytics device */}
                  <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
                    <div className="w-20 h-20 bg-indigo-500 rounded-2xl flex items-center justify-center shadow-lg relative">
                      <TrendingUp className="w-8 h-8 text-white animate-pulse" />
                      {/* Data visualization bars */}
                      <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex gap-1">
                        <div className="w-2 h-4 bg-white/60 rounded-full"></div>
                        <div className="w-2 h-6 bg-white/80 rounded-full"></div>
                        <div className="w-2 h-3 bg-white/60 rounded-full"></div>
                        <div className="w-2 h-7 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating elements representing analytics data */}
                  <div className="absolute top-8 right-8 w-6 h-6 bg-blue-400 rounded-full shadow-lg animate-bounce">
                    <Activity className="w-4 h-4 text-white m-1" />
                  </div>
                  <div className="absolute bottom-8 left-8 w-4 h-4 bg-indigo-400 rounded-full shadow-lg animate-bounce delay-300">
                    <Target className="w-3 h-3 text-white m-0.5" />
                  </div>
                  <div className="absolute top-16 left-16 w-5 h-5 bg-purple-400 rounded-full shadow-lg animate-bounce delay-500">
                    <Award className="w-3 h-3 text-white m-1" />
                  </div>
                </div>
                
                {/* Wanderloop+ badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-3 shadow-xl">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Unlock Your Walking Potential <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
                with Advanced Analytics
              </span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed">
              Advanced Analytics is an exclusive feature for premium members—<br />
              discover deep insights about your walking habits and achievements.
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Features List */}
              <div>
                <h2 className="text-3xl font-black text-gray-900 mb-8">
                  With Advanced Analytics, You Can:
                </h2>
                
                <div className="space-y-6">
                  {[
                    {
                      icon: <TrendingUp className="h-6 w-6" />,
                      title: "Track your walking progress",
                      description: "detailed stats on distance, time, and elevation",
                      color: "from-blue-500 to-cyan-500"
                    },
                    {
                      icon: <Target className="h-6 w-6" />,
                      title: "Get smart recommendations",
                      description: "AI-powered suggestions based on your habits",
                      color: "from-green-500 to-emerald-500"
                    },
                    {
                      icon: <Award className="h-6 w-6" />,
                      title: "Unlock personal achievements",
                      description: "celebrate milestones and track goals",
                      color: "from-purple-500 to-pink-500"
                    },
                    {
                      icon: <Navigation className="h-6 w-6" />,
                      title: "Discover walking patterns",
                      description: "insights into your favorite routes and areas",
                      color: "from-orange-500 to-red-500"
                    }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-4 group">
                      <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-110`}>
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900 mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Demo Preview */}
              <div>
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-xl">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl text-gray-900">Analytics Dashboard</h3>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Premium Feature</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-6">
                    {/* Stats Preview */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: "Total Walks", value: "47", icon: Activity },
                        { label: "Distance", value: "97 mi", icon: MapPin },
                        { label: "Elevation", value: "7,677 ft", icon: Mountain },
                        { label: "Time", value: "38h 25m", icon: Clock }
                      ].map((stat, index) => (
                        <div key={index} className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-blue-200">
                          <div className="flex items-center gap-2 mb-1">
                            <stat.icon className="h-4 w-4 text-blue-600" />
                            <span className="text-xs text-gray-600">{stat.label}</span>
                          </div>
                          <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                        </div>
                      ))}
                    </div>
                    
                    {/* Chart Preview */}
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <span className="font-semibold text-gray-900 text-sm">Weekly Progress</span>
                      </div>
                      <div className="flex items-end gap-2 h-16">
                        {[40, 65, 45, 80, 55, 70, 90].map((height, index) => (
                          <div key={index} className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t" style={{ height: `${height}%` }}></div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Insights Preview */}
                    <div className="bg-gradient-to-r from-green-100 to-blue-100 p-4 rounded-lg border-l-4 border-green-400">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🧠</span>
                        <span className="font-bold text-green-800">Personal Insight</span>
                      </div>
                      <p className="text-sm text-gray-700">You walk 340% more on Sunday mornings than any other time. You've stopped at 12 unique restaurants and 8 cultural landmarks across your walks — and you've never repeated a route twice.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>

          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-black mb-8">
              Ready to Discover Your Walking Insights?
            </h2>

            <p className="text-xl mb-12 text-blue-100 max-w-2xl mx-auto">
              Track your progress, unlock achievements, and get personalized recommendations.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Button
                size="lg"
                onClick={() => window.location.href = '/membership'}
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-black px-12 py-4 text-xl shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <Crown className="mr-3 h-6 w-6" />
                Become a Founding Member
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </div>

            <div className="flex items-center justify-center gap-8 text-sm text-blue-100">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <span>Founding rate locked in</span>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your analytics...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-20">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchDashboardData} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { stats, recommendations, recent_activities, this_month, insights } = dashboardData || {};

  // Show test data component if user is premium but has no data
  if (stats && stats.total_walks === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Walking Analytics</h1>
            <p className="text-gray-600">Start tracking your walks to see detailed insights</p>
          </div>

          <AnalyticsTestData />

          <div className="text-center py-8">
            <Button onClick={fetchDashboardData} className="mt-4">
              Refresh Dashboard
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Format numbers for display
  const formatDistance = (meters) => {
    const miles = meters / 1609.34;
    if (miles >= 0.1) {
      return `${miles.toFixed(1)} mi`;
    }
    return `${Math.round(meters * 3.281)} ft`;
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Prepare chart data
  const monthlyData = [
    { name: 'This Month', walks: this_month.walks, distance: this_month.distance_km }
  ];

  const locationData = stats.unique_neighborhoods.slice(0, 5).map(neighborhood => ({
    name: neighborhood,
    value: Math.floor(Math.random() * 5) + 1 // Mock data for visits
  }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Walking Analytics</h1>
          <p className="text-gray-600">Discover insights about your walking habits and achievements</p>
        </div>

        {/* Key Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Walks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_walks}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <MapPin className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Distance</p>
                <p className="text-2xl font-bold text-gray-900">{formatDistance(stats.total_distance_meters)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <Mountain className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Elevation Gain</p>
                <p className="text-2xl font-bold text-gray-900">{Math.round(stats.total_elevation_gain_meters * 3.281)} ft</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Time</p>
                <p className="text-2xl font-bold text-gray-900">{formatTime(stats.total_time_minutes)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Personal Insights */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Personal Insights
              </h3>
              <div className="space-y-4">
                {insights.map((insight, index) => (
                  <div key={index} className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-gray-800">{insight}</p>
                  </div>
                ))}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center mb-2">
                      <Map className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Neighborhoods Explored</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.unique_neighborhoods.length}</p>
                    <div className="flex flex-wrap mt-2">
                      {stats.unique_neighborhoods.slice(0, 3).map((neighborhood, index) => (
                        <Badge key={index} variant="secondary" className="mr-1 mb-1 text-xs">
                          {neighborhood}
                        </Badge>
                      ))}
                      {stats.unique_neighborhoods.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{stats.unique_neighborhoods.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center mb-2">
                      <Camera className="w-4 h-4 text-purple-600 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Photos Taken</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_photos_taken}</p>
                    <div className="flex items-center mt-2 text-sm text-gray-600">
                      <Users className="w-4 h-4 mr-1" />
                      <span>{stats.walks_with_companions} walks with friends</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* This Month Progress */}
          <div>
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                This Month
              </h3>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Walks Completed</span>
                    <span className="text-sm font-medium text-gray-900">{this_month.walks}</span>
                  </div>
                  <Progress value={(this_month.walks / 10) * 100} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1">Goal: 10 walks</p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Distance</span>
                    <span className="text-sm font-medium text-gray-900">{(this_month.distance_km * 0.6214).toFixed(1)} mi</span>
                  </div>
                  <Progress value={(this_month.distance_km / 50) * 100} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1">Goal: 31 mi</p>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Most Explored Areas</h4>
                  <div className="space-y-2">
                    {stats.unique_neighborhoods.slice(0, 3).map((neighborhood, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        <span className="text-gray-700">{neighborhood}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Smart Recommendations */}
        <Card className="p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Star className="w-5 h-5 mr-2" />
            Smart Recommendations for You
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recommendations.map((recommendation) => (
              <div 
                key={recommendation.id}
                className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer"
                onClick={() => handleRecommendationClick(recommendation.id, recommendation.walk_id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <Badge variant="secondary" className="text-xs">
                    {recommendation.recommendation_type.replace(/_/g, ' ')}
                  </Badge>
                  <div className="flex items-center text-xs text-gray-500">
                    <Star className="w-3 h-3 mr-1" />
                    {Math.round(recommendation.confidence_score * 100)}%
                  </div>
                </div>
                <h4 className="font-medium text-gray-900 mb-2">{recommendation.title}</h4>
                <p className="text-sm text-gray-600 mb-3">{recommendation.reasoning}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center">
                    <Navigation className="w-3 h-3 mr-1" />
                    {recommendation.distance_meters && formatDistance(recommendation.distance_meters)}
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {recommendation.duration_minutes && formatTime(recommendation.duration_minutes)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Activities */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Recent Activities
          </h3>
          <div className="space-y-4">
            {recent_activities.slice(0, 5).map((activity, index) => (
              <div key={index} className="flex items-center p-4 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">
                      {activity.location || 'Walk Activity'}
                    </p>
                    <span className="text-sm text-gray-500">
                      {new Date(activity.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 mt-1">
                    <span className="mr-4">{formatDistance(activity.distance_meters || 0)}</span>
                    <span className="mr-4">{formatTime(activity.duration_minutes || 0)}</span>
                    {activity.photos_taken > 0 && (
                      <span className="flex items-center">
                        <Camera className="w-4 h-4 mr-1" />
                        {activity.photos_taken}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default AnalyticsDashboard;